import { useState, useMemo, useCallback } from 'react';
import {
  Download,
  Package,
  AlertTriangle,
  Loader2,
  Search,
  Printer,
  Filter,
  DollarSign,
  Percent,
  AlertCircle,
  MapPin,
  ChevronRight,
  ChevronDown,
  Calendar,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { useAssetReport, type MarginWarning } from '@/hooks/useAssetReport';
import { useLocations } from '@/hooks/useLocations';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';

const MARGIN_WARNING_CONFIG: Record<NonNullable<MarginWarning>, { label: string; color: string; description: string }> = {
  no_cost: {
    label: '100%',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
    description: 'No cost recorded - margin appears as 100%',
  },
  zero_margin: {
    label: '0%',
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    description: 'Price equals cost - no profit margin',
  },
  negative_margin: {
    label: 'Neg',
    color: 'bg-red-100 text-red-800 border-red-300',
    description: 'Cost exceeds price - selling at a loss',
  },
  very_high: {
    label: '>80%',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    description: 'Very high margin - verify cost is correct',
  },
};

export default function AssetReport() {
  const { organization } = useAuth();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'Asset Report' });

  // Filters
  const [selectedLocationId, setSelectedLocationId] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [includeZero, setIncludeZero] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'margin_asc' | 'margin_desc' | 'value_desc' | 'sold_desc'>('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [showWarningsOnly, setShowWarningsOnly] = useState(false);

  // Date range for historical data
  const [dateFrom, setDateFrom] = useState<Date>(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState<Date>(new Date());

  // Track expanded products
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['categories', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      // categories are location-scoped (no organization_id column); resolve the
      // org's categories through their location.
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, location:locations!inner(organization_id)')
        .eq('location.organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch suppliers
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  const {
    groupedProducts,
    assetItems,
    warningCounts,
    summary,
    isLoading,
    error,
    downloadCSV,
  } = useAssetReport({
    locationId: selectedLocationId === 'all' ? undefined : selectedLocationId,
    categoryId: categoryFilter === 'all' ? undefined : categoryFilter,
    vendorId: vendorFilter === 'all' ? undefined : vendorFilter,
    includeZero,
    sortBy,
    dateFrom,
    dateTo,
  });

  // Filter products by search and warnings
  const filteredProducts = useMemo(() => {
    let products = showWarningsOnly
      ? groupedProducts.filter(p => p.margin_warning !== null)
      : groupedProducts;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      products = products.filter(
        (product) =>
          product.product_name.toLowerCase().includes(query) ||
          (product.sku && product.sku.toLowerCase().includes(query))
      );
    }

    return products;
  }, [groupedProducts, searchQuery, showWarningsOnly]);

  // Toggle product expansion
  const toggleExpand = useCallback((productId: string) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  // Expand/collapse all
  const expandAll = useCallback(() => {
    setExpandedProducts(new Set(filteredProducts.map(p => p.product_id)));
  }, [filteredProducts]);

  const collapseAll = useCallback(() => {
    setExpandedProducts(new Set());
  }, []);

  const getMarginBadge = (marginPercent: number, marginWarning: MarginWarning) => {
    if (marginWarning) {
      const config = MARGIN_WARNING_CONFIG[marginWarning];
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className={`${config.color} border`}>
                <AlertCircle className="h-3 w-3 mr-1" />
                {config.label}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>{config.description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <span className={`font-medium ${marginPercent >= 30 ? 'text-green-600' : marginPercent >= 15 ? 'text-blue-600' : 'text-muted-foreground'}`}>
        {marginPercent.toFixed(1)}%
      </span>
    );
  };

  // Format value or show dash for zero/null
  const formatValue = (value: number | null | undefined, isCurrency = false) => {
    if (value === null || value === undefined || value === 0) {
      return <span className="text-muted-foreground">-</span>;
    }
    return isCurrency ? formatCurrency(value) : value.toLocaleString();
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return <span className="text-muted-foreground">-</span>;
    return format(new Date(dateStr), 'MMM d, yyyy');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Asset Report</h1>
          <p className="text-sm text-muted-foreground">
            Inventory valuation with sales history by location
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={downloadCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Items</span>
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summary?.total_items || 0}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.total_units?.toLocaleString() || 0} units
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Cost</span>
              <DollarSign className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summary?.total_cost || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Inventory at cost</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Retail Value</span>
              <DollarSign className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summary?.total_retail || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">At current prices</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Avg Margin</span>
              <Percent className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : `${summary?.average_margin?.toFixed(1) || 0}%`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Profit: {formatCurrency(summary?.potential_profit || 0)}
            </p>
          </CardContent>
        </Card>
        <Card className={summary?.items_needing_review ? 'border-amber-300' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Needs Review</span>
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold mt-2 text-amber-600">
              {isLoading ? '...' : summary?.items_needing_review || 0}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Margin issues</p>
          </CardContent>
        </Card>
      </div>

      {/* Warning Breakdown */}
      {(warningCounts.no_cost > 0 || warningCounts.zero_margin > 0 || warningCounts.negative_margin > 0) && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium">Margin Issues:</span>
              </div>
              {warningCounts.no_cost > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={MARGIN_WARNING_CONFIG.no_cost.color}>
                    No Cost
                  </Badge>
                  <span className="text-sm">{warningCounts.no_cost} items</span>
                </div>
              )}
              {warningCounts.zero_margin > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={MARGIN_WARNING_CONFIG.zero_margin.color}>
                    0% Margin
                  </Badge>
                  <span className="text-sm">{warningCounts.zero_margin} items</span>
                </div>
              )}
              {warningCounts.negative_margin > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={MARGIN_WARNING_CONFIG.negative_margin.color}>
                    Negative
                  </Badge>
                  <span className="text-sm">{warningCounts.negative_margin} items</span>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowWarningsOnly(!showWarningsOnly)}
              >
                {showWarningsOnly ? 'Show All' : 'Show Issues Only'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
          <SelectTrigger className="w-[180px]">
            <MapPin className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories?.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={vendorFilter} onValueChange={setVendorFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vendors</SelectItem>
            {suppliers?.map((supplier) => (
              <SelectItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="margin_asc">Margin (Low to High)</SelectItem>
            <SelectItem value="margin_desc">Margin (High to Low)</SelectItem>
            <SelectItem value="value_desc">Value (High to Low)</SelectItem>
            <SelectItem value="sold_desc">Items Sold (High to Low)</SelectItem>
          </SelectContent>
        </Select>

        {/* Date Range Picker */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Calendar className="h-4 w-4" />
              {format(dateFrom, 'MMM d')} - {format(dateTo, 'MMM d, yyyy')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="flex">
              <div className="border-r p-3">
                <p className="text-sm font-medium mb-2">From</p>
                <CalendarComponent
                  mode="single"
                  selected={dateFrom}
                  onSelect={(date) => date && setDateFrom(date)}
                  initialFocus
                />
              </div>
              <div className="p-3">
                <p className="text-sm font-medium mb-2">To</p>
                <CalendarComponent
                  mode="single"
                  selected={dateTo}
                  onSelect={(date) => date && setDateTo(date)}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="include-zero"
            checked={includeZero}
            onCheckedChange={setIncludeZero}
          />
          <Label htmlFor="include-zero" className="text-sm">
            Include zero stock
          </Label>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            Error loading asset report. Please try again.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Inventory Assets</CardTitle>
              <CardDescription>
                {filteredProducts.length} products
                {showWarningsOnly && ' with margin issues'}
                {selectedLocationId === 'all' && locations.length > 1 && (
                  <span className="ml-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-3.5 w-3.5 inline text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Click a product row to see location breakdown</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                )}
              </CardDescription>
            </div>
            {selectedLocationId === 'all' && locations.length > 1 && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={expandAll}>
                  Expand All
                </Button>
                <Button variant="ghost" size="sm" onClick={collapseAll}>
                  Collapse All
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="w-[300px]">
                      <div className="flex items-center gap-2">
                        <span>SKU name</span>
                        <span className="text-muted-foreground font-normal">Outlet</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center border-l">
                      <div className="text-xs text-muted-foreground uppercase tracking-wide">Total</div>
                      <div className="font-medium">Current inventory</div>
                    </TableHead>
                    <TableHead className="text-center border-l" colSpan={3}>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide flex items-center justify-center gap-1">
                        Historical
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-3 w-3" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Data from {format(dateFrom, 'MMM d')} - {format(dateTo, 'MMM d, yyyy')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableHead>
                    <TableHead className="text-center border-l">Inbound inventory</TableHead>
                    <TableHead className="text-center border-l">Last sale</TableHead>
                    <TableHead className="text-right border-l">Margin</TableHead>
                  </TableRow>
                  <TableRow className="bg-muted/30 hover:bg-muted/30 text-xs">
                    <TableHead></TableHead>
                    <TableHead className="text-center border-l"></TableHead>
                    <TableHead className="text-center border-l">Items sold</TableHead>
                    <TableHead className="text-center">Revenue</TableHead>
                    <TableHead className="text-center">Avg Cost</TableHead>
                    <TableHead className="text-center border-l"></TableHead>
                    <TableHead className="text-center border-l"></TableHead>
                    <TableHead className="text-right border-l"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.slice(0, 100).map((product) => {
                    const isExpanded = expandedProducts.has(product.product_id);
                    const hasMultipleLocations = product.location_count > 1;
                    const canExpand = selectedLocationId === 'all' && hasMultipleLocations;

                    return (
                      <>
                        {/* Product Row */}
                        <TableRow
                          key={product.product_id}
                          className={cn(
                            'group',
                            canExpand && 'cursor-pointer hover:bg-muted/50',
                            product.margin_warning === 'negative_margin' && 'bg-red-50 dark:bg-red-950/20'
                          )}
                          onClick={() => canExpand && toggleExpand(product.product_id)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {canExpand ? (
                                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              ) : (
                                <div className="w-6" />
                              )}
                              <Checkbox className="shrink-0" onClick={(e) => e.stopPropagation()} />
                              <div className="min-w-0">
                                <div className="font-medium truncate">{product.product_name}</div>
                                {product.sku && (
                                  <div className="text-xs text-muted-foreground">{product.sku}</div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center tabular-nums border-l font-medium">
                            {product.total_quantity}
                          </TableCell>
                          <TableCell className="text-center tabular-nums border-l">
                            {formatValue(product.total_items_sold)}
                          </TableCell>
                          <TableCell className="text-center tabular-nums">
                            {formatValue(product.total_revenue, true)}
                          </TableCell>
                          <TableCell className="text-center tabular-nums">
                            {formatCurrency(product.average_cost)}
                          </TableCell>
                          <TableCell className="text-center tabular-nums border-l">
                            {formatValue(product.total_inbound)}
                          </TableCell>
                          <TableCell className="text-center border-l">
                            {formatDate(product.last_sale_date)}
                          </TableCell>
                          <TableCell className="text-right border-l">
                            {getMarginBadge(product.margin_percent, product.margin_warning)}
                          </TableCell>
                        </TableRow>

                        {/* Location Rows (when expanded) */}
                        {isExpanded && product.locations.map((location) => (
                          <TableRow
                            key={`${product.product_id}-${location.location_id}`}
                            className="bg-muted/20 hover:bg-muted/30"
                          >
                            <TableCell>
                              <div className="flex items-center gap-2 pl-14">
                                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <span className="text-sm text-muted-foreground">
                                  {location.location_name}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center tabular-nums border-l">
                              {location.quantity_on_hand}
                            </TableCell>
                            <TableCell className="text-center tabular-nums border-l">
                              {formatValue(location.items_sold)}
                            </TableCell>
                            <TableCell className="text-center tabular-nums">
                              {formatValue(location.revenue, true)}
                            </TableCell>
                            <TableCell className="text-center tabular-nums">
                              {formatCurrency(location.average_cost)}
                            </TableCell>
                            <TableCell className="text-center tabular-nums border-l">
                              {formatValue(location.inbound_inventory)}
                            </TableCell>
                            <TableCell className="text-center border-l">
                              {formatDate(location.last_sale_date)}
                            </TableCell>
                            <TableCell className="text-right border-l">
                              {getMarginBadge(location.margin_percent, location.margin_warning as MarginWarning)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    );
                  })}
                  {filteredProducts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No inventory items found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            {filteredProducts.length > 100 && (
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Showing first 100 of {filteredProducts.length} products
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Print template */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Asset Report</h1>
            <div className="print-meta">
              {selectedLocationId !== 'all'
                ? locations.find((l) => l.id === selectedLocationId)?.name
                : 'All Locations'}
            </div>
          </div>
          <div className="print-meta">Generated on {new Date().toLocaleString()}</div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Total Cost</div>
            <div className="value">{formatCurrency(summary?.total_cost || 0)}</div>
          </div>
          <div className="print-card">
            <div className="label">Retail Value</div>
            <div className="value">{formatCurrency(summary?.total_retail || 0)}</div>
          </div>
          <div className="print-card">
            <div className="label">Avg Margin</div>
            <div className="value">{summary?.average_margin?.toFixed(1) || 0}%</div>
          </div>
          <div className="print-card">
            <div className="label">Potential Profit</div>
            <div className="value">{formatCurrency(summary?.potential_profit || 0)}</div>
          </div>
        </div>

        <div className="print-section">
          <table className="print-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Location</th>
                <th>QOH</th>
                <th>Avg Cost</th>
                <th>Total Cost</th>
                <th>Items Sold</th>
                <th>Revenue</th>
                <th>Margin</th>
              </tr>
            </thead>
            <tbody>
              {assetItems.map((item) => (
                <tr key={`${item.product_id}-${item.location_id}`}>
                  <td>{item.product_name}</td>
                  <td>{item.location_name || '-'}</td>
                  <td>{item.quantity_on_hand}</td>
                  <td>{formatCurrency(item.average_cost)}</td>
                  <td>{formatCurrency(item.total_cost)}</td>
                  <td>{item.items_sold}</td>
                  <td>{formatCurrency(item.revenue)}</td>
                  <td>{item.margin_percent.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
